import { formatServices } from "../../utils/providerFormatters";
import { parseAndMapSpecializations } from "../../utils/specializationMappings";
import {
  getProviderTypeAffinity,
  getTypicalCommunitySuggestEntries,
  resolveProviderTypeAffinityKey,
  suggestLabelForCanonical,
} from "../../utils/providerTypeServiceAffinity";
import { resolveRareAffinityBusinessType } from "../../utils/providerSearchCareTypeDisplay";
import { SERVICE_LABEL_TO_CANONICAL } from "../../generated/careTaxonomy";
import { serviceDisplayKey } from "../../utils/serviceMappings";

/** Plan 29 P2.3 — muted tooltip suffix when chips are type-typical invent (flagged). */
export const TYPE_DEFAULT_CHIP_HINT = "Typical for type";

/** List-card safety cap: high enough that a typical rail overflows and clips. */
export const CARD_CAPABILITY_CHIP_LIMIT = 12;
export const CARD_A11Y_CHIP_LIMIT = 2;
/** Dotted funding/language meta line: visible tokens before "+N more". */
export const CARD_FUNDING_LANGUAGE_TOKEN_LIMIT = 4;
export const CARD_FUNDING_CHIP_LIMIT = 2;
export const CARD_LANGUAGE_CHIP_LIMIT = 2;
/**
 * Legacy soft-max constants (count-based rail). List cards now clip overflow in
 * ProviderCardChipRail; keep exports for any external references/tests.
 */
export const CARD_TOTAL_CHIP_LIMIT_COMPACT = 4;
/** Workspace / short-laptop density. */
export const CARD_TOTAL_CHIP_LIMIT_DENSE = 3;
export const CARD_TOTAL_CHIP_LIMIT_DESKTOP = 5;

/**
 * Minnesota community-service ingests use this as a legacy fallback when a
 * record has no specific service. The provider type already communicates that
 * broad category, so it is not useful (or actionable) as a card service chip.
 */
const GENERIC_LEGACY_SERVICE_LABELS = new Set(["community services"]);

/**
 * Clinical services that are rare for ADL home_care — hide on home_care cards.
 * Compare against display labels from ``formatServices`` (e.g. "Skilled Nursing"
 * → "RN Oversight & Skilled Nursing Support") so remapped names still match.
 *
 * Plan 32 B9: ``filterBusinessType`` only overrides rare affinity when the row
 * is a search peer of that filter (see ``resolveRareAffinityBusinessType``).
 */
function rareServicesForBusinessType(
  businessType: string | undefined,
  filterBusinessType?: string | null
): Set<string> {
  const peerAffinity = resolveRareAffinityBusinessType(
    businessType,
    filterBusinessType
  );
  const affinityKey = peerAffinity || businessType?.trim() || undefined;
  const rare = getProviderTypeAffinity(affinityKey)?.rare_general;
  if (!rare?.length) return new Set();
  return new Set(
    formatServices(JSON.stringify([...rare]))
      .map((s) => s.trim().toLowerCase())
      .filter(Boolean)
  );
}

/** Plan 32 B7: Home Health Care chip repeats Home Health Agency type. */
const HHA_TAUTOLOGY_LABELS = new Set([
  "home health care",
  "home health",
  "home health aide",
  "home health aide services",
]);

function isHhaTautologyChip(
  label: string,
  businessType: string | undefined
): boolean {
  const key = resolveProviderTypeAffinityKey(businessType);
  if (key !== "home_health") return false;
  return HHA_TAUTOLOGY_LABELS.has(label.trim().toLowerCase());
}

/** True unless the label is the generic MN ingest placeholder "Community Services". */
export function isDisplayableLegacyServiceLabel(value: string): boolean {
  return !GENERIC_LEGACY_SERVICE_LABELS.has(value.trim().toLowerCase());
}

function isDisplayableLegacyService(value: string): boolean {
  return isDisplayableLegacyServiceLabel(value);
}

function isRareCapabilityLabel(value: string, rare: Set<string>): boolean {
  return rare.has(value.trim().toLowerCase());
}

export function truncateChipLabel(label: string, maxLen = 24): string {
  const s = (label || "").trim();
  if (!s) return "";
  return s.length > maxLen ? `${s.slice(0, maxLen - 3)}...` : s;
}

/**
 * Prefer suggest-style short labels on compact cards (Plan 32 B3) so suggest
 * chips and list-card chips share one display policy.
 */
export function compactChipDisplayLabel(
  label: string,
  preferShort = false
): string {
  const trimmed = (label || "").trim();
  if (!trimmed) return "";
  if (!preferShort) {
    return truncateChipLabel(trimmed);
  }
  const canonical =
    SERVICE_LABEL_TO_CANONICAL[trimmed] ??
    SERVICE_LABEL_TO_CANONICAL[
      Object.keys(SERVICE_LABEL_TO_CANONICAL).find(
        (key) => key.toLowerCase() === trimmed.toLowerCase()
      ) ?? ""
    ] ??
    serviceDisplayKey(trimmed);
  if (canonical) {
    const short = suggestLabelForCanonical(canonical);
    if (short) return short;
    if (canonical.length <= trimmed.length) return canonical;
  }
  return trimmed;
}

/**
 * Type-typical services for card display when the provider row has no usable
 * services_offered / specializations. Prefer stored provider values whenever
 * present — never merge these defaults on top of real data.
 *
 * Community-led types (BH / HCBS / case management) invent from
 * ``typical_community_keys`` so cards match search “common supports”, not
 * misleading general labels like Companionship.
 *
 * Only apply when `useTypeDefaultsWhenEmpty` is true. Callers that merge with
 * source-backed community chips must leave it false so empty legacy fields do
 * not invent chips beside real community evidence.
 */
function typeDefaultServiceLabels(
  businessType: string | undefined,
  rare: Set<string>
): string[] {
  const communityTypical = getTypicalCommunitySuggestEntries(businessType);
  if (communityTypical.length > 0) {
    return communityTypical
      .map((entry) => entry.label)
      .filter(
        (label) =>
          isDisplayableLegacyService(label) &&
          !rare.has(label.trim().toLowerCase())
      );
  }
  const typical = getProviderTypeAffinity(businessType)?.typical_general;
  if (!typical?.length) return [];
  return formatServices(JSON.stringify([...typical])).filter(
    (s) =>
      isDisplayableLegacyService(s) && !rare.has(s.trim().toLowerCase())
  );
}

export function deriveProviderCardCapabilityChips(
  servicesRaw: string,
  specializationsRaw: string,
  options?: {
    preferShortLabels?: boolean;
    limit?: number;
    /** When home_care, suppress affinity-rare clinical chips (SN/PT/OT/ST). */
    businessType?: string | null;
    /**
     * Active search filter Care Type. Rare suppress uses this affinity only
     * when the row is a search peer of the filter (Plan 32 B9).
     */
    rareAffinityBusinessType?: string | null;
    /**
     * When true and stored services/specializations yield no displayable chips,
     * fall back to provider-type typicals (community keys when present, else
     * typical_general).
     */
    useTypeDefaultsWhenEmpty?: boolean;
  }
): {
  chips: Array<{
    label: string;
    fullLabel: string;
    source: "service" | "specialization";
  }>;
  totalCount: number;
  hiddenCount: number;
  /** True when chips came from affinity typicals (empty stored fields). */
  usedTypeDefaults: boolean;
} {
  const businessType = options?.businessType ?? undefined;
  const rare = rareServicesForBusinessType(
    businessType,
    options?.rareAffinityBusinessType
  );
  // Placeholder-filtered stored labels (before rare suppression). Used so we
  // never invent type defaults when the row had real stored evidence that was
  // only hidden by affinity-rare rules (A7) — Plan 29 stored-wins / D4.
  const storedServices = formatServices(servicesRaw || "").filter(
    isDisplayableLegacyService
  );
  const storedSpecializations = parseAndMapSpecializations(
    specializationsRaw || ""
  ).filter(isDisplayableLegacyService);
  const hadStoredLegacy =
    storedServices.length > 0 || storedSpecializations.length > 0;

  const services = storedServices.filter(
    (s) => !isRareCapabilityLabel(s, rare)
  );
  const specializations = storedSpecializations.filter(
    (s) => !isRareCapabilityLabel(s, rare)
  );
  let combined = [
    ...services.map((s) => ({ value: s, source: "service" as const })),
    ...specializations.map((s) => ({
      value: s,
      source: "specialization" as const,
    })),
  ];

  let usedTypeDefaults = false;
  if (
    combined.length === 0 &&
    options?.useTypeDefaultsWhenEmpty &&
    !hadStoredLegacy
  ) {
    const defaults = typeDefaultServiceLabels(businessType, rare);
    if (defaults.length > 0) {
      usedTypeDefaults = true;
      combined = defaults.map((s) => ({
        value: s,
        source: "service" as const,
      }));
    }
  }

  const seen = new Set<string>();
  let unique = combined.filter((item) => {
    const key = item.value.trim().toLowerCase();
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });

  // Plan 32 B7: omit tautological Home Health Care when HHA already shows
  // other clinical chips (or when inventing a full typical rail).
  const hasNonTautology = unique.some(
    (item) => !isHhaTautologyChip(item.value, businessType)
  );
  if (hasNonTautology) {
    unique = unique.filter(
      (item) => !isHhaTautologyChip(item.value, businessType)
    );
  }

  // `limit` defaults to the card cap; callers that merge with other chip sources
  // (e.g. source-backed community chips) pass a larger cap and let downstream slicing
  // account for overflow, so nothing is silently dropped before dedup.
  const limit = options?.limit ?? CARD_CAPABILITY_CHIP_LIMIT;
  const visible = unique.slice(0, limit);
  const preferShort = options?.preferShortLabels ?? false;
  return {
    chips: visible.map((item) => ({
      label: compactChipDisplayLabel(item.value, preferShort),
      fullLabel: item.value,
      source: item.source,
    })),
    totalCount: unique.length,
    hiddenCount: Math.max(0, unique.length - visible.length),
    usedTypeDefaults,
  };
}
